<template>
  <teleport to="body">
    <div v-if="open" class="fixed inset-0 z-[70] overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="$emit('cancel')"></div>

      <!-- Modal -->
      <div class="flex min-h-full items-center justify-center p-4 text-center">
        <div
          class="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all relative"
          @click.stop
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              :class="iconBgClass"
            >
              <component :is="iconComponent" class="h-5 w-5" :class="iconClass" />
            </div>
            <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              {{ title }}
            </h3>
          </div>

          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {{ message }}
          </p>

          <div class="flex justify-end gap-3">
            <button
              v-if="secondaryLabel"
              type="button"
              @click="$emit('secondary')"
              :disabled="isLoading"
              class="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              {{ secondaryLabel }}
            </button>
            <button
              type="button"
              @click="$emit('cancel')"
              :disabled="isLoading"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {{ cancelLabel }}
            </button>
            <button
              type="button"
              @click="$emit('confirm')"
              :disabled="isLoading"
              class="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              :class="confirmButtonClass"
            >
              <span v-if="isLoading" class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Bezig...
              </span>
              <span v-else>{{ confirmLabel }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: 'Bevestigen'
  },
  message: {
    type: String,
    required: true
  },
  confirmLabel: {
    type: String,
    default: 'Bevestigen'
  },
  cancelLabel: {
    type: String,
    default: 'Annuleren'
  },
  secondaryLabel: {
    type: String,
    default: ''
  },
  variant: {
    type: String,
    default: 'success',
    validator: value => ['success', 'warning', 'danger'].includes(value)
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['confirm', 'cancel', 'secondary'])

const iconComponent = computed(() => {
  return props.variant === 'success' ? CheckCircleIcon : ExclamationTriangleIcon
})

const iconBgClass = computed(() => {
  const classes = {
    success: 'bg-green-100 dark:bg-green-900/50',
    warning: 'bg-yellow-100 dark:bg-yellow-900/50',
    danger: 'bg-red-100 dark:bg-red-900/50'
  }
  return classes[props.variant]
})

const iconClass = computed(() => {
  const classes = {
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    danger: 'text-red-600 dark:text-red-400'
  }
  return classes[props.variant]
})

const confirmButtonClass = computed(() => {
  const classes = {
    success: 'bg-green-600 hover:bg-green-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    danger: 'bg-red-600 hover:bg-red-700'
  }
  return classes[props.variant]
})

// Handle Escape key
const handleKeydown = e => {
  if (e.key === 'Escape' && props.open && !props.isLoading) {
    emit('cancel')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>
